import { atom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { fetchTemplatesAssignments } from '../../queries/KPITemplatesQueries';
import { portcosAtomJ } from '../PortCosStateJ';
import { TemplateAssignment } from '../../../schemas/template-assignment-schema';

/** UI: id of the assignment currently being edited (-1 when none). */
export const kpisSelectedTemplateAssignmentStateJ = atom<number>(-1);

/**
 * Source of truth for all KPI template assignments — async fetch on first
 * subscribe.
 */
export const kpiTemplateAssignmentsListStateJ = atomWithDefault<
  TemplateAssignment[] | Promise<TemplateAssignment[]>
>(() => fetchTemplatesAssignments());

/**
 * Map of assignment id → assignment, derived from
 * {@link kpiTemplateAssignmentsListStateJ}.
 */
export const kpiTemplateAssignmentsByIdStateJ = atom(async (get) => {
  const list = await get(kpiTemplateAssignmentsListStateJ);
  const map = new Map<number, TemplateAssignment>();
  if (!list) {
    return map;
  }
  list.forEach((assignment) => {
    map.set(assignment.id, assignment);
  });
  return map;
});

/** Map of companyId → assignments for that company. */
export const templateAssignmentsByCompanyIdStateJ = atom(async (get) => {
  const list = (await get(kpiTemplateAssignmentsListStateJ)) ?? [];
  return list.reduce((acc, assignment) => {
    acc.has(assignment.companyId)
      ? acc.get(assignment.companyId)!.push(assignment)
      : acc.set(assignment.companyId, [assignment]);
    return acc;
  }, new Map<number, TemplateAssignment[]>());
});

/**
 * Map of companyId → set of assigned template uuids.
 */
export const assignedTemplateUuidsByCompanyIdStateJ = atom(async (get) => {
  const assignmentsByCompanyId = await get(templateAssignmentsByCompanyIdStateJ);
  const uuidsByCompanyId = new Map<number, Set<string>>();
  assignmentsByCompanyId.forEach((assignments, companyId) => {
    uuidsByCompanyId.set(companyId, new Set(assignments.map((a) => a.templateUuid)));
  });
  return uuidsByCompanyId;
});

/** Portfolio companies with no template assignments. */
export const kpiUnassignedCompaniesListStateJ = atom<Promise<ICompanyDataModel[] | null>>(async (get) => {
  const portfolioCompanies = get(portcosAtomJ);
  const list = await get(kpiTemplateAssignmentsListStateJ);
  const assignmentsCompaniesId = list?.map((assignment) => assignment.companyId);
  return portfolioCompanies.filter((company) => company && !assignmentsCompaniesId?.includes(company.id));
});
